import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  getProject,
  selectProjects,
  updateProject,
  getStartupScopes,
  selectProjectScopes,
  selectIsFetchingScopes,
  addScope,
} from '../../../store/slices/projectSlice';
import { useInvalidateStartups } from '../../../queries/useStartups';
import { projectService } from '../../../api/services/project.service';
import type { ScopeDto } from '../../../api/services/project.service';
import type { ScopeStatus } from '../../../types/conversation.types';

export interface StartupInfoData {
  name: string;
  industry: string;
  target_icp: string;
  problem_details: string;
}

// Matches what ScopeCard + CreateScopeDialog + StartupPageTemplate expect
export interface StartupScope {
  id: string;
  title: string;
  name: string;
  description?: string | null;
  status: 'planned' | 'in_progress' | 'blocked' | 'completed';
  startupId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

export interface UseStartupPageFacadeParams {
  navigateToScope: (scopeId: string) => void;
  startupId?: string | null;
  /**
   * When false, the facade skips its on-mount fetches (the single project and
   * the scopes list). Use this when a consumer only needs the create/edit-scope
   * *actions* (e.g. the shared shell header's "New Scope" button) and must not
   * pull the scope list on pages that don't show it — which otherwise fires a
   * stray `getStartupScopes` on Home/Artifacts and double-fetches on Work
   * Scopes (header facade + page facade). Defaults to true.
   */
  autoFetch?: boolean;
  /**
   * When false, skips only the scopes-list fetch (the project fetch still runs).
   * Use this when the caller drives its own paginated/searchable scopes list
   * (see `useScopesList`) so the facade doesn't also populate the Redux `scopes`
   * array and double-fetch the endpoint on the Work Scopes page. Defaults to true.
   */
  fetchScopes?: boolean;
}

export interface UseStartupPageFacadeReturn {
  startup: any | null;
  isLoadingStartup: boolean;
  brandSyncEnabled: boolean;
  updateStartupInfo: (
    data: StartupInfoData & { newFiles?: File[]; contentSourceFiles?: File[] }
  ) => Promise<void>;
  scopes: StartupScope[];
  isLoadingScopes: boolean;
  activeFilter: ScopeStatus | 'all';
  filteredScopes: StartupScope[];
  scopeCounts: Record<ScopeStatus | 'all', number>;
  setFilter: (filter: ScopeStatus | 'all') => void;
  navigateToScope: (id: string) => void;
  refetchScopes: () => void;
  createScope: (data: {
    title: string;
    description?: string;
    newFiles?: File[];
  }) => Promise<string | undefined>;
  updateScope: (
    id: string,
    data: { title?: string; description?: string; status?: ScopeStatus; newFiles?: File[] },
  ) => Promise<void>;
  deleteScope: (id: string) => Promise<void>;
  fetchScopeAttachments: (scopeId: string) => Promise<{ id: string; fileName: string; fileSize?: number }[]>;
  deleteScopeAttachment: (attachmentId: string) => Promise<void>;
  fetchStartupAttachments: () => Promise<{ id: string; fileName: string; fileSize?: number }[]>;
  deleteStartupAttachment: (attachmentId: string) => Promise<void>;
  isCreatingScope: boolean;
  isMutatingScope: boolean;
  createDialogOpen: boolean;
  editingScope: StartupScope | null;
  openCreateDialog: () => void;
  openEditDialog: (scope: StartupScope) => void;
  closeDialog: () => void;
}

export function useStartupPageFacade({ navigateToScope, startupId, autoFetch = true, fetchScopes = true }: UseStartupPageFacadeParams): UseStartupPageFacadeReturn {
  const dispatch = useAppDispatch();
  const projects = useAppSelector(selectProjects);
  const scopes = useAppSelector(selectProjectScopes);
  const planDetails = useAppSelector((state: any) => state.app.planDetails);
  const invalidateStartups = useInvalidateStartups();

  const [isLoadingStartup, setIsLoadingStartup] = useState(false);
  const [isLoadingScopes, setIsLoadingScopes] = useState(false);
  const [activeFilter, setActiveFilter] = useState<ScopeStatus | 'all'>('all');
  const [isCreatingScope, setIsCreatingScope] = useState(false);
  const [isMutatingScope, setIsMutatingScope] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingScope, setEditingScope] = useState<StartupScope | null>(null);
  const fetchedForStartupId = useRef<string | null>(null);

  const resolvedStartupId = startupId ?? null;

  // Startup === Project. Look up the project being viewed by id; fall back to
  // the first project so existing UI doesn't break before the right one loads.
  const projectStartup =
    (resolvedStartupId
      ? projects?.find((p: any) => p.id === resolvedStartupId)
      : null) ?? projects?.[0] ?? null;

  // Single source of truth is the project row. (We previously merged in a name
  // from the startupEntities list, but that list lags edits — it caused stale
  // names on the header after Update startup info.)
  const startup = projectStartup
    ? { ...projectStartup, title: projectStartup.name }
    : null;

  // Fetch the specific project being viewed if not already in the slice.
  useEffect(() => {
    if (!autoFetch) return;
    if (!resolvedStartupId) return;
    const alreadyLoaded = projects?.some((p: any) => p.id === resolvedStartupId);
    if (alreadyLoaded) return;
    setIsLoadingStartup(true);
    dispatch(getProject(resolvedStartupId)).finally(() => setIsLoadingStartup(false));
  }, [autoFetch, dispatch, projects, resolvedStartupId]);

  // Fetch scopes for the startup
  useEffect(() => {
    if (!autoFetch || !fetchScopes) return;
    if (resolvedStartupId && fetchedForStartupId.current !== resolvedStartupId) {
      fetchedForStartupId.current = resolvedStartupId;
      setIsLoadingScopes(true);
      dispatch(getStartupScopes({ startupId: resolvedStartupId })).finally(() =>
        setIsLoadingScopes(false),
      );
    }
  }, [autoFetch, fetchScopes, dispatch, resolvedStartupId]);

  // Map ScopeDto → StartupScope (bridge old template field names)
  const scopeList: StartupScope[] = (scopes ?? []).map((s: ScopeDto) => ({
    id: s.id,
    name: s.name,
    title: s.name,             // ScopeCard uses .title
    description: s.description,
    status: 'planned' as ScopeStatus,  // projects don't have status; default for UI
    startupId: s.startupId,
    createdAt: s.createdAt,
    updatedAt: s.updatedAt,
  }));

  const filteredScopes = activeFilter === 'all'
    ? scopeList
    : scopeList.filter((s) => s.status === activeFilter);

  const scopeCounts = {
    all: scopeList.length,
    planned: scopeList.filter((s) => s.status === 'planned').length,
    in_progress: scopeList.filter((s) => s.status === 'in_progress').length,
    blocked: scopeList.filter((s) => s.status === 'blocked').length,
    completed: scopeList.filter((s) => s.status === 'completed').length,
  };

  const brandSyncEnabled = useMemo(() => {
    const feature = planDetails?.features?.find((f: any) => f.name === 'Update Design System');
    return feature?.maxLimit === 'unlimited';
  }, [planDetails]);

  const updateStartupInfo = useCallback(async (data: StartupInfoData & { newFiles?: File[]; contentSourceFiles?: File[] }) => {
    if (!startup?.id) return;
    const { newFiles, contentSourceFiles, ...projectData } = data;
    await dispatch(updateProject({ projectId: startup.id, projectData: projectData as any })).unwrap();

    // Upload any files attached in the drawer. Best-effort — log on failure.
    for (const file of newFiles ?? []) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        await projectService.uploadProjectFiles(startup.id, fd);
      } catch (err) {
        console.error('Failed to upload startup file', file.name, err);
      }
    }

    // Content-source files are uploaded with extract_tone=true so the AI-API
    // learns the product's tone of voice (same path as onboarding's
    // "Attach content source file" step).
    for (const file of contentSourceFiles ?? []) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        await projectService.uploadProjectFiles(startup.id, fd, { extractTone: true });
      } catch (err) {
        console.error('Failed to upload content source file', file.name, err);
      }
    }

    // Refetch the project (for the explore-page header + drawer fields) and
    // invalidate the startups list (so My Startups cards reflect the new name too).
    dispatch(getProject(startup.id));
    invalidateStartups();
  }, [dispatch, startup?.id, invalidateStartups]);

  const refetchScopes = useCallback(() => {
    if (resolvedStartupId) {
      dispatch(getStartupScopes({ startupId: resolvedStartupId }));
    }
  }, [dispatch, resolvedStartupId]);

  const uploadScopeFiles = useCallback(async (scopeId: string, files: File[]) => {
    if (!resolvedStartupId) return;
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        await projectService.uploadScopeAttachment(resolvedStartupId, scopeId, fd);
      } catch (err) {
        console.error('[useStartupPageFacade] failed to upload scope file', file.name, err);
      }
    }
  }, [resolvedStartupId]);

  // CreateScopeDialog sends { title } — map to { name } for API
  const createScope = useCallback(async (data: { title: string; description?: string; newFiles?: File[] }) => {
    if (!resolvedStartupId) return;
    setIsCreatingScope(true);
    try {
      const created = await projectService.createScope(resolvedStartupId, {
        name: data.title,
        description: data.description,
      });
      // Show the new scope in the list instantly instead of waiting on the
      // refetch round-trip (the source of the ~1–2s lag). The refetch below
      // still runs to reconcile; addScope is deduped by id.
      if (created?.id) {
        dispatch(addScope(created));
      }
      if (data.newFiles?.length && created?.id) {
        await uploadScopeFiles(created.id, data.newFiles);
      }
      refetchScopes();
      return created?.id;
    } finally {
      setIsCreatingScope(false);
    }
  }, [resolvedStartupId, refetchScopes, uploadScopeFiles]);

  const updateScope = useCallback(async (
    id: string,
    data: { title?: string; description?: string; status?: ScopeStatus; newFiles?: File[] },
  ) => {
    if (!resolvedStartupId) return;
    setIsMutatingScope(true);
    try {
      await projectService.updateScope(resolvedStartupId, id, {
        ...(data.title !== undefined && { name: data.title }),
        description: data.description,
      });
      if (data.newFiles?.length) {
        await uploadScopeFiles(id, data.newFiles);
      }
      refetchScopes();
    } finally {
      setIsMutatingScope(false);
    }
  }, [resolvedStartupId, refetchScopes, uploadScopeFiles]);

  const fetchScopeAttachments = useCallback(async (scopeId: string) => {
    if (!resolvedStartupId) return [];
    try {
      const list = await projectService.getScopeAttachments(resolvedStartupId, scopeId);
      return list.map((a) => ({
        id: a.id,
        fileName: a.name || 'Untitled',
        fileSize: a.fileSize ?? undefined,
      }));
    } catch (err) {
      console.error('[useStartupPageFacade] failed to fetch scope attachments', err);
      return [];
    }
  }, [resolvedStartupId]);

  const deleteScopeAttachment = useCallback(async (attachmentId: string) => {
    await projectService.deleteScopeAttachment(attachmentId);
  }, []);

  // Startup files = the underlying project's attachments — same endpoints
  // (`/projects/:id/attachments`) under a clearer name for the startup drawer.
  const fetchStartupAttachments = useCallback(async () => {
    if (!startup?.id) return [];
    try {
      const res = await projectService.getProjectAttachments(startup.id);
      const list = Array.isArray(res) ? res : (res?.data ?? []);
      return list.map((a: any) => ({
        id: a.id,
        fileName: a.fileName ?? a.name ?? a.originalName ?? 'Untitled',
        fileSize: a.fileSize ?? a.size,
      }));
    } catch (err) {
      console.error('[useStartupPageFacade] failed to fetch startup attachments', err);
      return [];
    }
  }, [startup?.id]);

  const deleteStartupAttachment = useCallback(async (attachmentId: string) => {
    await projectService.deleteProjectAttachment(attachmentId);
  }, []);

  const deleteScope = useCallback(async (id: string) => {
    if (!resolvedStartupId) return;
    setIsMutatingScope(true);
    try {
      await projectService.deleteScope(resolvedStartupId, id);
      refetchScopes();
    } finally {
      setIsMutatingScope(false);
    }
  }, [resolvedStartupId, refetchScopes]);

  return {
    startup,
    isLoadingStartup,
    brandSyncEnabled,
    updateStartupInfo,
    scopes: scopeList,
    isLoadingScopes,
    activeFilter,
    filteredScopes,
    scopeCounts,
    setFilter: setActiveFilter,
    navigateToScope,
    refetchScopes,
    createScope,
    updateScope,
    deleteScope,
    fetchScopeAttachments,
    deleteScopeAttachment,
    fetchStartupAttachments,
    deleteStartupAttachment,
    isCreatingScope,
    isMutatingScope,
    createDialogOpen,
    editingScope,
    openCreateDialog: () => { setEditingScope(null); setCreateDialogOpen(true); },
    openEditDialog: (scope) => { setEditingScope(scope); setCreateDialogOpen(true); },
    closeDialog: () => { setCreateDialogOpen(false); setEditingScope(null); },
  };
}
